import React, { useState } from "react";
import { pricingData } from "../data/mock";
import { Check, Star, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="precios" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Planes y Precios
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            El plan perfecto para <span className="text-blue-600">cada negocio</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Desde pequeños comercios hasta grandes empresas, tenemos la solución que se adapta 
            perfectamente a tus necesidades y presupuesto.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            <span className={`text-lg ${!isAnnual ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Mensual
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                isAnnual ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  isAnnual ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-lg ${isAnnual ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Anual
            </span>
            {isAnnual && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                Ahorra 20%
              </span>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pricingData.map((plan, index) => {
            const annualPrice = Math.round(plan.price * 0.8);
            const colorClasses = {
              gray: "border-gray-200 bg-white",
              blue: "border-blue-300 bg-blue-50 ring-4 ring-blue-100 transform scale-105",
              purple: "border-purple-200 bg-white"
            };

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-8 ${colorClasses[plan.color]} transition-all duration-300 hover:shadow-2xl`}
                style={{animationDelay: `${index * 100}ms`}}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center space-x-1">
                      <Star size={16} className="fill-current" />
                      <span>Más Popular</span>
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-gray-900">
                        ${isAnnual ? annualPrice : plan.price}
                      </span>
                      <span className="text-xl text-gray-600 ml-2">
                        /{isAnnual ? 'mes' : plan.period}
                      </span>
                    </div>
                    {isAnnual && (
                      <div className="text-sm text-green-600 mt-2">
                        Facturado anualmente (${annualPrice * 12})
                      </div>
                    )}
                  </div>

                  <Button 
                    className={`w-full py-3 text-lg font-semibold transition-all ${
                      plan.popular 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    {plan.popular ? 'Iniciar Prueba Gratuita' : 'Elegir Plan'}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-gray-900 mb-4">
                    Todo incluido:
                  </div>
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <Check className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {plan.popular && (
                  <div className="mt-8 p-4 bg-blue-100 rounded-xl border border-blue-200">
                    <div className="text-sm text-blue-800 text-center">
                      <strong>💡 Incluye:</strong> Migración gratuita de datos + 2 horas de capacitación personal
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Preguntas Frecuentes sobre Precios
          </h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                ¿Puedo cambiar de plan en cualquier momento?
              </h4>
              <p className="text-gray-600 mb-6">
                Sí, puedes actualizar o degradar tu plan cuando lo necesites. 
                Los cambios se aplican inmediatamente y solo pagas la diferencia prorrateada.
              </p>

              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                ¿Hay costos adicionales por implementación?
              </h4>
              <p className="text-gray-600">
                No, la implementación, migración de datos y capacitación inicial 
                están incluidas sin costo adicional en todos los planes.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                ¿Ofrecen descuentos para múltiples sucursales?
              </h4>
              <p className="text-gray-600 mb-6">
                Sí, ofrecemos descuentos progresivos desde 10% para 3+ sucursales. 
                Contacta a nuestro equipo comercial para una cotización personalizada.
              </p>

              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                ¿Qué incluye la garantía?
              </h4>
              <p className="text-gray-600">
                Ofrecemos 30 días de garantía de satisfacción completa. Si no estás 
                100% satisfecho, te devolvemos tu dinero, sin preguntas.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            ¿Necesitas un plan personalizado para tu empresa?
          </p>
          <Button variant="outline" size="lg" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
            Solicitar Cotización Empresarial
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Pricing;